import React, { useEffect, useMemo, useState } from 'react';
import { Tab } from 'semantic-ui-react';
import { EEhsStatus, IBuilding, IEhs, IUser } from '@relab/alstom-ptw-shared';
import { connect } from 'react-redux';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { ehsAction, ehsUpdateAction, resetServerRequestAction } from '../../redux/actions';
import { ActionType, RootState, ServerRequestStatus } from '../../redux/types';
import { appState, appStateServerRequests } from '../../redux/selectors';
import { closureValidationSchema, creationValidationSchema, headlineValidationSchema } from './ehsValidationSchemas';
import { closureInitialValues, EhsHeadlineComponent, EhsPhaseClosure, EhsPhaseCreation } from './ehsFormComponents';
import { useTranslation } from 'react-i18next';

interface EhsViewComponentProps {
  ehsId: string;
  buildings: IBuilding[];
  onCancel: () => void;
  onUpdated: (ehs: IEhs) => void;
  ehs: IEhs;
  ehsAction: typeof ehsAction;
  ehsUpdateAction: typeof ehsUpdateAction;
  resetServerRequestAction: typeof resetServerRequestAction;
  updateEhsServerRequest?: ServerRequestStatus;
  user: IUser;
}

const PEhsViewComponent: React.FC<EhsViewComponentProps> = ({
  ehsId,
  ehsAction,
  ehs,
  buildings,
  onCancel,
  onUpdated,
  ehsUpdateAction,
  resetServerRequestAction,
  updateEhsServerRequest,
  user,
}) => {
  const [requestError, setRequestError] = useState<string | undefined>();

  useEffect(() => {
    ehsAction(ehsId);
  }, [ehsAction, ehsId]);

  useEffect(() => {
    if (updateEhsServerRequest && updateEhsServerRequest.success) {
      resetServerRequestAction(ActionType.EHS_UPDATE);
      onUpdated(updateEhsServerRequest?.payload?.data);
    } else if (updateEhsServerRequest?.error) {
      setRequestError(updateEhsServerRequest.error);
    }
  }, [onUpdated, resetServerRequestAction, updateEhsServerRequest]);

  const onSubmit = (values: Partial<IEhs>) => {
    ehsUpdateAction(values, { attachments: values.attachments, ehsClosureFile: values.ehsClosureFile });
  };

  const { t } = useTranslation();

  const transValidationSchema = useMemo(() => closureValidationSchema(t), [t]);
  const transHeadlineValidationSchema = useMemo(() => headlineValidationSchema(t), [t]);
  const transCreationValidationSchema = useMemo(() => creationValidationSchema(t), [t]);

  const validationSchema = useMemo(() => {
    const validationSchema = Yup.object().shape({
      ...transHeadlineValidationSchema.fields,
      ...transCreationValidationSchema.fields,
    });

    if (ehs && ehs.status >= EEhsStatus.ACCETTAZIONE_Y) {
      return validationSchema.concat(transValidationSchema);
    } else {
      return validationSchema;
    }
  }, [ehs, transCreationValidationSchema.fields, transHeadlineValidationSchema.fields, transValidationSchema]);

  const panes = useMemo(() => {
    const panesMemo = [
      {
        menuItem: 'Create',
        render: () => (
          <Tab.Pane>
            <EhsPhaseCreation buildings={buildings} ehsType={ehs.ehsType} userRoles={user.userRoles} />
          </Tab.Pane>
        ),
      },
    ];

    if (ehs && ehs.status >= EEhsStatus.ACCETTAZIONE_Y) {
      panesMemo.push({
        menuItem: 'Close',
        render: () => (
          <Tab.Pane>
            <EhsPhaseClosure />
          </Tab.Pane>
        ),
      });
    }

    return panesMemo;
  }, [buildings, ehs, user.userRoles]);

  if (!ehs || !buildings) {
    return null;
  } else {
    return (
      <Formik
        initialValues={{
          ...ehs,
          ...closureInitialValues,
          ossigenoValorePrima: ehs.ossigenoValorePrima ?? '',
          ossigenoValoreDurante: ehs.ossigenoValoreDurante ?? '',
          ossigenoValoreTermine: ehs.ossigenoValoreTermine ?? '',
          monossidoValorePrima: ehs.monossidoValorePrima ?? '',
          monossidoValoreDurante: ehs.monossidoValoreDurante ?? '',
          monossidoValoreTermine: ehs.monossidoValoreTermine ?? '',
          metanoValorePrima: ehs.metanoValorePrima ?? '',
          metanoValoreDurante: ehs.metanoValoreDurante ?? '',
          metanoValoreTermine: ehs.metanoValoreTermine ?? '',
          idrogenoValorePrima: ehs.idrogenoValorePrima ?? '',
          idrogenoValoreDurante: ehs.idrogenoValoreDurante ?? '',
          idrogenoValoreTermine: ehs.idrogenoValoreTermine ?? '',
          imbracatureSiNo: ehs.imbracatureSiNo ?? '',
          imbracatureDateLast: ehs.imbracatureDateLast ?? '',
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <Form className={'form ui'}>
          <EhsHeadlineComponent
            ehs={ehs}
            onCancel={onCancel}
            onDelete={() => onSubmit({ _id: ehsId, status: EEhsStatus.CANCELLATO })}
            requestError={requestError}
            user={user}
          />

          <Tab panes={panes} />
        </Form>
      </Formik>
    );
  }
};

const mapStateToProps = (state: RootState) => ({
  ehs: appState(state).ehs,
  user: appState(state).user,
  updateEhsServerRequest: appStateServerRequests(state, ActionType.EHS_UPDATE),
});

export const EhsViewComponent = connect(mapStateToProps, {
  ehsAction,
  ehsUpdateAction,
  resetServerRequestAction,
})(PEhsViewComponent);
